import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GroupedSelect, RangeSliderExtended, SelectWithSearch } from 'ui';
import { ratingTypes } from 'shared/constants/ratingTypes';
import cloneDeep from 'lodash/cloneDeep';
import { CountryParsed, EntityInitialFiltersData, FiltersData, RangeFilter } from '../../types';

import {
  minMaxValueInitialValues,
  rangeSliderValuesGenerator,
  updateFilters,
  valueParser,
} from '../../utils';

type DatatableEntityFiltersProps = {
  filterFieldsInitialData: EntityInitialFiltersData;
  prevFiltersValues: FiltersData;
  handleSubmit: (value: FiltersData) => void;
  ratingTypePage: ratingTypes | undefined;
};

export const DatatableEntityFilters: FC<DatatableEntityFiltersProps> = ({
  filterFieldsInitialData,
  handleSubmit,
  ratingTypePage,
  prevFiltersValues,
}) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);
  const id = open ? 'datatable-filters-popover' : undefined;
  const [isFiltered, setIsFiltered] = useState(false);

  const [dataToSubmit, setDataToSubmit] = useState<FiltersData>({});

  const {
    countryCode,
    creditRating,
    creditRatingShortTermRating,
    creditReview,
    esgEnvironment,
    esgGovernance,
    esgSocial,
    esgTotal,
    industry,
    sigCountryRiskAssessment,
    sigPOD,
    storefrontRatingOutlook,
    storefrontRatingType,
  } = filterFieldsInitialData;
  const countriesParsed = useMemo(() => {
    const countriesList: CountryParsed[] = [];
    if (countryCode.data) {
      countryCode.data.forEach((region: any) => {
        const { continentCode, continent, countries } = region;
        countries.forEach((countryInRegion: any) => {
          const { name, iso } = countryInRegion;
          countriesList.push({
            continentCode,
            continent,
            name,
            iso,
          });
        });
      });
    }
    return countriesList;
  }, [countryCode]);

  const industriesParsed = useMemo(() => {
    const industriesList: any = [];
    if (industry.data) {
      industry.data.forEach((industryObj: any) => {
        const { sectors } = industryObj;
        sectors.forEach((sector: { code: string; name: 'string' }) =>
          industriesList.push({
            name: sector.name,
            value: sector.name,
          }),
        );
      });
    }
    return industriesList;
  }, [industry]);

  const ratingParsed = valueParser(creditRating.length ? creditRating : []);
  const storefrontRatingOutlookParsed = valueParser(storefrontRatingOutlook);
  const shortTermRatingsParsed = valueParser(creditRatingShortTermRating);
  const sigCountryRiskAssessmentParsed = valueParser(sigCountryRiskAssessment);
  const creditReviewParsed = useMemo(
    () => rangeSliderValuesGenerator(creditReview, 1),
    [creditReview],
  );
  const sigPODParsed = useMemo(() => rangeSliderValuesGenerator(sigPOD), [sigPOD]);
  const esgEnvironmentParsed = useMemo(
    () => rangeSliderValuesGenerator(esgEnvironment),
    [esgEnvironment],
  );
  const esgSocialParsed = useMemo(() => rangeSliderValuesGenerator(esgSocial), [esgSocial]);
  const esgGovernanceParsed = useMemo(
    () => rangeSliderValuesGenerator(esgGovernance),
    [esgGovernance],
  );
  const esgTotalParsed = useMemo(() => rangeSliderValuesGenerator(esgTotal), [esgTotal]);

  const values = {
    countries: countriesParsed,
    industries: industriesParsed,
    storefrontRatingType: storefrontRatingType.map((element) => {
      return {
        label: t(`PORTFOLIO.${element}`),
        value: element,
      };
    }),
    rating: minMaxValueInitialValues(ratingParsed),
    storefrontRatingOutlookParsed,
    shortTermRatings: minMaxValueInitialValues(shortTermRatingsParsed),
    sigCountryRiskAssessment: minMaxValueInitialValues(sigCountryRiskAssessmentParsed),
    creditReview: minMaxValueInitialValues(creditReviewParsed),
    pod: minMaxValueInitialValues(sigPODParsed),
    environmental: minMaxValueInitialValues(esgEnvironmentParsed),
    social: [esgSocial?.minimum, esgSocial?.maximum],
    governance: minMaxValueInitialValues(esgGovernanceParsed),
    total: minMaxValueInitialValues(esgTotalParsed),
  };

  const handleCountryChange = (value: any) => {
    setDataToSubmit({
      ...dataToSubmit,
      countryCode: value,
    });
  };

  const handleSelectChange = (name: keyof FiltersData) => (value: any) =>
    setDataToSubmit(updateFilters(dataToSubmit, name, value));

  const handleRangeSliderChange = (name: string) => (newValues: RangeFilter) => {
    setDataToSubmit({
      ...dataToSubmit,
      [name]: newValues,
    });
  };

  const generatePreviousValues = () => {
    const previousFilterValues = cloneDeep(prevFiltersValues);
    if (previousFilterValues.countryCode) {
      const countriesConverted = previousFilterValues.countryCode.map((countryIso: string) =>
        countriesParsed.find((element: any) => element.iso === countryIso),
      );
      previousFilterValues.countryCode = countriesConverted as unknown as string[];
    }
    if (previousFilterValues.industry) {
      const industriesConverted = previousFilterValues.industry.map((item: string) =>
        industriesParsed.find((searchedIndustry: any) => searchedIndustry.value === item),
      );
      previousFilterValues.industry = industriesConverted as unknown as string[];
    }
    return prevFiltersValues ? previousFilterValues : {};
  };

  const checkFiltersAvailability = () => {
    if (ratingTypePage === ratingTypes.ESG_IMPACT) {
      return (
        !!esgEnvironmentParsed.length ||
        !!esgSocialParsed.length ||
        !!esgGovernanceParsed.length ||
        !!esgTotalParsed.length
      );
    }

    if (ratingTypePage === ratingTypes.CREDIT_REVIEW) {
      return (
        !!creditReviewParsed.length || !!sigCountryRiskAssessment.length || !!sigPODParsed.length
      );
    }

    if (ratingTypePage === ratingTypes.CREDIT_RATING) {
      return (
        !!values.countries.length ||
        !!values.industries.length ||
        !!values.storefrontRatingType.length ||
        !!ratingParsed.length ||
        !!storefrontRatingOutlookParsed.length ||
        !!shortTermRatingsParsed.length
      );
    }

    if (ratingTypePage === ratingTypes.SENTIMENT) {
      return null;
    }

    if (ratingTypePage === ratingTypes.CLIMATE_STRESS_TEST) {
      return null;
    }

    return !!values.countries.length || !!values.industries.length;
  };

  useEffect(() => {
    if (!open) {
      setIsFiltered(!!Object.keys(prevFiltersValues).length);
      setDataToSubmit(generatePreviousValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prevFiltersValues]);

  return (
    <>
      {checkFiltersAvailability() && (
        <Button
          size="small"
          variant={isFiltered ? 'contained' : 'outlined'}
          color={isFiltered ? 'primary' : undefined}
          onClick={handleClick}
        >
          {isFiltered ? t('Change filters') : t('Apply filters')}
        </Button>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2} width="500px">
          {ratingTypePage !== ratingTypes.CREDIT_REVIEW &&
            ratingTypePage !== ratingTypes.ESG_IMPACT && (
              <>
                {/* COUNTRIES */}
                {!!values.countries.length && (
                  <Box pb={2}>
                    <GroupedSelect
                      multiple
                      options={values.countries}
                      value={dataToSubmit.countryCode ?? []}
                      label={t('PORTFOLIO.COUNTRY')}
                      onChange={handleCountryChange}
                      getOptionLabel={(option: any) => option.name}
                      groupBy={(option: any) => option.continent}
                    />
                  </Box>
                )}
                {/* INDUSTRIES */}
                {!!values.industries.length && (
                  <GroupedSelect
                    multiple
                    options={values.industries}
                    value={dataToSubmit.industry ?? []}
                    label={t('PORTFOLIO.INDUSTRIES')}
                    onChange={handleSelectChange('industry')}
                    getOptionLabel={(option: any) => option.name}
                    groupBy={(option: any) => option.continent}
                  />
                )}
              </>
            )}
          {ratingTypePage === ratingTypes.CREDIT_RATING && (
            <>
              {/* ENTITY RATING  */}
              {!!values.storefrontRatingType.length && (
                <Box pt={2}>
                  <SelectWithSearch
                    label={t('PORTFOLIO.RATING_TYPE_ONELINE')}
                    options={values.storefrontRatingType}
                    value={dataToSubmit.storefrontRatingType ?? null}
                    onChange={handleSelectChange('storefrontRatingType')}
                  />
                </Box>
              )}
              {!!ratingParsed.length && (
                <RangeSliderExtended
                  label={t('PORTFOLIO.ENTITY_RATING')}
                  options={ratingParsed}
                  values={dataToSubmit.creditRating ?? values?.rating}
                  onChange={handleRangeSliderChange('creditRating')}
                />
              )}
              {/* OUTLOOK  */}
              {!!storefrontRatingOutlookParsed.length && (
                <Box pt={2}>
                  <SelectWithSearch
                    label={t('PORTFOLIO.OUTLOOK')}
                    options={storefrontRatingOutlookParsed}
                    value={dataToSubmit.storefrontRatingOutlook ?? null}
                    onChange={handleSelectChange('storefrontRatingOutlook')}
                  />
                </Box>
              )}
              {/* SHORT TERM RATINGS  */}
              {!!shortTermRatingsParsed.length && (
                <RangeSliderExtended
                  label={t('PORTFOLIO.SHORT_TERM_RATINGS')}
                  options={shortTermRatingsParsed}
                  values={dataToSubmit.creditRatingShortTermRating ?? values?.shortTermRatings}
                  onChange={handleRangeSliderChange('creditRatingShortTermRating')}
                />
              )}
            </>
          )}
          {ratingTypePage === ratingTypes.CREDIT_REVIEW && (
            <>
              {/* CREDIT ASSESMENT  */}
              {!!creditReviewParsed.length && (
                <RangeSliderExtended
                  label={t('PORTFOLIO.CREDIT_ASSESMENT')}
                  options={creditReviewParsed}
                  values={dataToSubmit.creditReview ?? values?.creditReview}
                  onChange={handleRangeSliderChange('creditReview')}
                />
              )}
              {/* COUNTRY RISK  */}
              {!!sigCountryRiskAssessment.length && (
                <RangeSliderExtended
                  label={t('PORTFOLIO.COUNTRY_RISK')}
                  options={sigCountryRiskAssessmentParsed}
                  values={dataToSubmit.sigCountryRiskAssessment ?? values?.sigCountryRiskAssessment}
                  onChange={handleRangeSliderChange('sigCountryRiskAssessment')}
                />
              )}
              {/* POD */}
              {!!sigPODParsed.length && (
                <RangeSliderExtended
                  label={t('PORTFOLIO.PROBABILITY_OF_DEFAULT_ONELINE')}
                  options={sigPODParsed}
                  values={dataToSubmit.sigPOD ?? values?.pod}
                  onChange={handleRangeSliderChange('sigPOD')}
                />
              )}
            </>
          )}
          {ratingTypePage === ratingTypes.ESG_IMPACT ||
            (ratingTypePage === ratingTypes.SENTIMENT && (
              <>
                {/* ENVIRONMENTAL */}
                {!!esgEnvironmentParsed.length && (
                  <RangeSliderExtended
                    label={t('PORTFOLIO.ENVIRONMENTAL')}
                    options={esgEnvironmentParsed}
                    values={dataToSubmit.esgEnvironment ?? values?.environmental}
                    onChange={handleRangeSliderChange('esgEnvironment')}
                  />
                )}
                {/* SOCIAL */}
                {!!esgSocialParsed.length && (
                  <RangeSliderExtended
                    label={t('PORTFOLIO.SOCIAL')}
                    options={esgSocialParsed}
                    values={dataToSubmit.esgSocial ?? values?.environmental}
                    onChange={handleRangeSliderChange('esgSocial')}
                  />
                )}
                {/* GOVERNANCE */}
                {!!esgGovernanceParsed.length && (
                  <RangeSliderExtended
                    label={t('PORTFOLIO.GOVERNANCE')}
                    options={esgGovernanceParsed}
                    values={dataToSubmit.esgGovernance ?? values?.governance}
                    onChange={handleRangeSliderChange('esgGovernance')}
                  />
                )}
                {/* TOTAL */}
                {!!esgTotalParsed.length && (
                  <RangeSliderExtended
                    label={t('PORTFOLIO.TOTAL')}
                    options={esgTotalParsed}
                    values={dataToSubmit.esgTotal ?? values?.total}
                    onChange={handleRangeSliderChange('esgTotal')}
                  />
                )}
              </>
            ))}
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button type="submit" onClick={() => setDataToSubmit({})}>
              {t('RESET')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              onClick={() => {
                handleSubmit(dataToSubmit);
                handleClose();
              }}
            >
              {t('APPLY_FILTERS')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
